import SubmitContext from 'context/submit/submitContext'
import PageContext from 'context/page/pageContext'
import FrozenVegsFruitsProduct from 'models/FrozenVegsFruitsProduct'
import React, { useContext, useEffect, useState } from 'react'
import Select from 'react-select'
import Switch from 'react-switch'
import {
  Button,
  ButtonGroup,
  Col,
  FormGroup,
  Input,
  Row,
  Alert,
} from 'reactstrap'
import { useFormUtils } from 'views/shared/useFormUtils'
import {
  fruitTypeOptions,
  packageTypeOptions,
  packagingInitialState,
  productTypeOptions,
  vegTypeOptions,
} from './constants'
import { Controller, ErrorMessage, useFormContext } from 'react-hook-form'
import { isNotAlpha, isNotNumericDecimal } from 'context/Utilities'

const FrozenVegsFruitsPackage = () => {
  const submitContext = useContext(SubmitContext)
  const pageContext = useContext(PageContext)

  const { current, setCurrent, updating } = submitContext
  const { setScrollToTop } = pageContext

  const [packaging, setPackaging] = useState(packagingInitialState)

  const { setSelect, onChange, setSwitch, asterisk } = useFormUtils()

  const [typeSelectOptions, setTypeSelectOptions] = useState(fruitTypeOptions)

  // Setup the "Product Type" select and "Type" select controls
  // based upon the current type of product
  const configureProductType = prodType => {
    const isFruitType = current?.FrozenProductType === productTypeOptions[0]
    setTypeSelectOptions(isFruitType ? fruitTypeOptions : vegTypeOptions)
  }

  const methods = useFormContext()

  useEffect(() => {
    setScrollToTop(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (
      current?.category === packagingInitialState.category &&
      current?.subCategory === packagingInitialState.subCategory
    ) {
      const {
        category,
        subCategory,
        ItemType,
        Size,
        PackageType,
        FrozenPackageCertification,
        OrganicVerification,
        FrozenProductType,
        otherType,
        otherPackageType,
      } = current
      setPackaging({
        category,
        subCategory,
        ItemType: {
          value: ItemType || '',
          label: ItemType.length > 0 ? ItemType : typeSelectOptions[0],
        },
        Size,
        PackageType: {
          value: PackageType || '',
          label: PackageType.length > 0 ? PackageType : 'Package Type',
        },
        FrozenPackageCertification,
        OrganicVerification,
        FrozenProductType,
        otherType,
        otherPackageType,
      })
    } else if (!updating) {
      setCurrent(FrozenVegsFruitsProduct)
      configureProductType('')
    }
    // eslint-disable-next-line
    if (current?.FrozenProductType === 'Fruit') {
      setTypeSelectOptions(fruitTypeOptions)
    } else {
      setTypeSelectOptions(vegTypeOptions)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current])

  // When "Product Type" selection changed
  // also change the "Type" selection options
  const setProductType = selected => {
    setTypeSelectOptions(
      selected === 'Fruit' ? fruitTypeOptions : vegTypeOptions
    )
    setCurrent({
      ...current,
      ItemType: '',
      otherType: '',
      packageType: '',
      otherPackageType: '',
    })
  }

  useEffect(() => {
    if (updating) {
      if (current?.PackageType === 'Other') {
        methods.setValue('otherPackageType', current.otherPackageType)
      }
      if (current?.ItemType === 'Other') {
        methods.setValue('otherType', current.otherType)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updating])

  return (
    <Row>
      <Col xs={12} md={6} lg={6}>
        <p>
          <strong>Frozen Product Type {asterisk}</strong>
        </p>
        <ButtonGroup>
          <Button
            outline
            color="success"
            className="btn-round"
            onClick={() => {
              setProductType(productTypeOptions[0])
              setCurrent({
                ...current,
                FrozenProductType: productTypeOptions[0],
                ItemType: '',
                otherType: '',
                PackageType: '',
                otherPackageType: '',
              })
              methods.reset({
                ItemType: '',
                otherType: '',
                PackageType: '',
                otherPackageType: '',
              })
            }}
            active={current?.FrozenProductType === productTypeOptions[0]}
            disabled={
              updating && current?.FrozenProductType !== productTypeOptions[0]
            }
            size="sm"
          >
            {productTypeOptions[0]}
          </Button>
        </ButtonGroup>{' '}
        <ButtonGroup>
          <Button
            outline
            color="success"
            className="btn-round"
            onClick={() => {
              setProductType(productTypeOptions[1])
              setCurrent({
                ...current,
                FrozenProductType: productTypeOptions[1],
                ItemType: '',
                otherType: '',
                PackageType: '',
                otherPackageType: '',
              })
              methods.reset({
                ItemType: '',
                otherType: '',
                PackageType: '',
                otherPackageType: '',
              })
            }}
            active={current?.FrozenProductType === productTypeOptions[1]}
            disabled={
              updating && current?.FrozenProductType !== productTypeOptions[1]
            }
            size="sm"
          >
            {productTypeOptions[1]}
          </Button>
        </ButtonGroup>{' '}
        <br />
        <br />
        <p>
          <strong>Type {asterisk}</strong>
        </p>
        <Controller
          as={Select}
          className="react-select primary"
          classNamePrefix="react-select"
          options={typeSelectOptions}
          name="ItemType"
          control={methods.control}
          rules={{ required: true }}
          placeholder={'Select Type'}
          onChange={([selected]) => {
            setSelect(selected, 'ItemType')
            return selected
          }}
        />
        <p className={'text-danger'}>
          <ErrorMessage
            errors={methods.errors}
            name="ItemType"
            message={'⚠ Type is required.'}
            as={<Alert color={'danger'} className={'mt-3'} />}
          />
        </p>
        <br />
        {current?.ItemType === 'Other' && (
          <FormGroup>
            <p>
              <strong>Other Type (Please Specify) {asterisk}</strong>
            </p>
            <Controller
              as={Input}
              name="otherType"
              placeholder={'Other Type'}
              defaultValue={current?.otherType}
              control={methods.control}
              rules={{
                required: true,
                maxLength: {
                  value: 15,
                  message: '⚠ Other Type exceeds 15 characters.',
                },
              }}
              onKeyDown={e => {
                if (isNotAlpha(e.key)) {
                  e.preventDefault()
                }
              }}
              onChange={([event]) => {
                onChange(event)
                return event
              }}
            />
            <p className={'text-danger'}>
              <ErrorMessage
                errors={methods.errors}
                name="otherType"
                message={'Other type is required.'}
                as={<Alert color={'danger'} className={'mt-3'} />}
              />
            </p>
          </FormGroup>
        )}
        <FormGroup>
          <p>
            <strong>Size {asterisk}</strong>
          </p>
        </FormGroup>
        <Controller
          as={Input}
          name="Size"
          placeholder={'Size'}
          control={methods.control}
          rules={{ required: true, maxLength: 5 }}
          onKeyDown={e => {
            if (isNotNumericDecimal(e.key)) {
              e.preventDefault()
            }
          }}
          onChange={([event]) => {
            onChange(event)
            return event
          }}
        />
        <p className={'text-danger'}>
          {methods.errors.Size && methods.errors.Size.type === 'required' && (
            <Alert color="danger" className={'mt-3'}>
              ⚠ Size is required.
            </Alert>
          )}
          {methods.errors.Size && methods.errors.Size.type === 'maxLength' && (
            <Alert color="danger" className={'mt-3'}>
              ⚠ Size exceeds 5 characters.
            </Alert>
          )}
        </p>
        <br />
      </Col>
      <Col xs={12} md={6} lg={6}>
        <p>
          <strong>Package Type {asterisk}</strong>
        </p>
        <Controller
          as={Select}
          className="react-select primary"
          classNamePrefix="react-select"
          options={packageTypeOptions}
          name="PackageType"
          control={methods.control}
          rules={{ required: true }}
          placeholder={'Select Package Type'}
          onChange={([selected]) => {
            setSelect(selected, 'PackageType')
            return selected
          }}
        />
        <p className={'text-danger'}>
          <ErrorMessage
            errors={methods.errors}
            name="PackageType"
            message={'⚠ This selection is required.'}
            as={<Alert color={'danger'} className={'mt-3'} />}
          />
        </p>
        <br />
        {current?.PackageType === 'Other' && (
          <FormGroup>
            <p>
              <strong>Other Package Type (Please Specify) {asterisk}</strong>
            </p>
            <Controller
              as={Input}
              name="otherPackageType"
              placeholder={'Other Package Type'}
              defaultValue={current?.otherPackageType}
              control={methods.control}
              rules={{
                required: true,
                maxLength: {
                  value: 15,
                  message: '⚠ Other Package Type exceeds 15 characters.',
                },
              }}
              onKeyDown={e => {
                if (isNotAlpha(e.key)) {
                  e.preventDefault()
                }
              }}
              onChange={([event]) => {
                onChange(event)
                return event
              }}
            />
            <p className={'text-danger'}>
              <ErrorMessage
                errors={methods.errors}
                name="otherPackageType"
                message={'Other package type is required.'}
                as={<Alert color={'danger'} className={'mt-3'} />}
              />
            </p>
          </FormGroup>
        )}

        <hr />
        <p>
          <strong>Organic</strong>
        </p>
        <Controller
          as={Switch}
          name="OrganicVerification"
          id="OrganicVerification"
          onChange={([checked, event, id]) => {
            setSwitch(checked, event, id)
            return checked
          }}
          control={methods.control}
          defaultValue={packaging.OrganicVerification}
        />
        <br />
        <hr />
        <p>
          <strong>
            I certify that this product has NO added sugars, added ingredients,
            or artificial sweeteners, such as Splenda or Nutrasweet. {asterisk}
          </strong>
        </p>
        <Controller
          as={Switch}
          name="FrozenPackageCertification"
          id="FrozenPackageCertification"
          onChange={([checked, event, id]) => {
            setSwitch(checked, event, id)
            return checked
          }}
          control={methods.control}
          defaultValue={packaging.FrozenPackageCertification}
          rules={{
            validate: FrozenPackageCertification =>
              FrozenPackageCertification === true,
          }}
        />
        <br />
        <p className={'text-danger'}>
          <ErrorMessage
            errors={methods.errors}
            name="FrozenPackageCertification"
            message={
              '⚠ Product must have NO added sugars, ingredients, or artificial sweeteners.'
            }
            as={<Alert color={'danger'} className={'mt-3'} />}
          />
        </p>
      </Col>
    </Row>
  )
}

export default FrozenVegsFruitsPackage
